import { ChangeEvent, FC, useState } from "react";
import { Image, Text, Input, Flex, useColorModeValue, Menu, Button, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { Label } from "./label";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

const countries = [
  {
    code: "BR",
    names: {
      "pt-BR": "Brasil",
      "en-US": "Brazil",
      "es-ES": "Brasil"
    },
    ddi: "+55",
    flag: "https://flagcdn.com/w40/br.png"
  },
  {
    code: "US",
    names: {
      "pt-BR": "Estados Unidos",
      "en-US": "United States",
      "es-ES": "Estados Unidos"
    },
    ddi: "+1",
    flag: "https://flagcdn.com/w40/us.png"
  },
  {
    code: "CA",
    names: {
      "pt-BR": "Canadá",
      "en-US": "Canada",
      "es-ES": "Canadá"
    },
    ddi: "+1",
    flag: "https://flagcdn.com/w40/ca.png"
  },
  {
    code: "PT",
    names: {
      "pt-BR": "Portugal",
      "en-US": "Portugal",
      "es-ES": "Portugal"
    },
    ddi: "+351",
    flag: "https://flagcdn.com/w40/pt.png"
  },
  {
    code: "AO",
    names: {
      "pt-BR": "Angola",
      "en-US": "Angola",
      "es-ES": "Angola"
    },
    ddi: "+244",
    flag: "https://flagcdn.com/w40/ao.png"
  },
  {
    code: "MZ",
    names: {
      "pt-BR": "Moçambique",
      "en-US": "Mozambique",
      "es-ES": "Mozambique"
    },
    ddi: "+258",
    flag: "https://flagcdn.com/w40/mz.png"
  },
  {
    code: "MX",
    names: {
      "pt-BR": "México",
      "en-US": "Mexico",
      "es-ES": "México"
    },
    ddi: "+52",
    flag: "https://flagcdn.com/w40/mx.png"
  },
  {
    code: "ES",
    names: {
      "pt-BR": "Espanha",
      "en-US": "Spain",
      "es-ES": "España"
    },
    ddi: "+34",
    flag: "https://flagcdn.com/w40/es.png"
  },
  {
    code: "GB",
    names: {
      "pt-BR": "Reino Unido",
      "en-US": "United Kingdom",
      "es-ES": "Reino Unido"
    },
    ddi: "+44",
    flag: "https://flagcdn.com/w40/gb.png"
  },
  {
    code: "AR",
    names: {
      "pt-BR": "Argentina",
      "en-US": "Argentina",
      "es-ES": "Argentina"
    },
    ddi: "+54",
    flag: "https://flagcdn.com/w40/ar.png"
  },
  {
    code: "CL",
    names: {
      "pt-BR": "Chile",
      "en-US": "Chile",
      "es-ES": "Chile"
    },
    ddi: "+56",
    flag: "https://flagcdn.com/w40/cl.png"
  },
  {
    code: "CO",
    names: {
      "pt-BR": "Colômbia",
      "en-US": "Colombia",
      "es-ES": "Colombia"
    },
    ddi: "+57",
    flag: "https://flagcdn.com/w40/co.png"
  },
  {
    code: "PE",
    names: {
      "pt-BR": "Peru",
      "en-US": "Peru",
      "es-ES": "Perú"
    },
    ddi: "+51",
    flag: "https://flagcdn.com/w40/pe.png"
  },
  {
    code: "VE",
    names: {
      "pt-BR": "Venezuela",
      "en-US": "Venezuela",
      "es-ES": "Venezuela"
    },
    ddi: "+58",
    flag: "https://flagcdn.com/w40/ve.png"
  },
  {
    code: "BO",
    names: {
      "pt-BR": "Bolívia",
      "en-US": "Bolivia",
      "es-ES": "Bolivia"
    },
    ddi: "+591",
    flag: "https://flagcdn.com/w40/bo.png"
  },
  {
    code: "HT",
    names: {
      "pt-BR": "Haiti",
      "en-US": "Haiti",
      "es-ES": "Haití"
    },
    ddi: "+509",
    flag: "https://flagcdn.com/w40/ht.png"
  },
  {
    code: "HN",
    names: {
      "pt-BR": "Honduras",
      "en-US": "Honduras",
      "es-ES": "Honduras"
    },
    ddi: "+504",
    flag: "https://flagcdn.com/w40/hn.png"
  },
  {
    code: "PA",
    names: {
      "pt-BR": "Panamá",
      "en-US": "Panama",
      "es-ES": "Panamá"
    },
    ddi: "+507",
    flag: "https://flagcdn.com/w40/pa.png"
  },
];

type PhoneInputProps = {
  value: string;
  onChange: (phone: string) => void;
};

const PhoneInput: FC<PhoneInputProps> = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();

  const getDefaultCountry = () => {
    const defaultCountryIndex = i18n.language.startsWith('pt') ? 0 : 1;

    const ddiMatch = value.match(/^\+(\d+)/);
    if (ddiMatch) {
      const matchingCountry = countries.find(country => country.ddi === `+${ddiMatch[1]}`);
      return matchingCountry || countries[defaultCountryIndex];
    }
    return countries[defaultCountryIndex];
  };

  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry);

  const handleDdiChange = (country: any) => {
    setSelectedCountry(country);
    onChange(`${country.ddi} ${value.replace(/^\+\d+\s?/, "")}`);
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(`${selectedCountry.ddi} ${event.target.value}`);
  };

  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  const phonePlaceholders = {
    'pt-BR': '81 99999-0000',
    'en-US': '555 123-4567',
    'es-ES': '555 123-4567'
  };

  const getPlaceholder = () => {
    if (i18n.language.startsWith('pt')) return phonePlaceholders['pt-BR'];
    if (i18n.language.startsWith('es')) return phonePlaceholders['es-ES'];
    return phonePlaceholders['en-US'];
  };

  return (
    <Flex direction={'column'} gap={3} my={3}>
      <Label name={'phone'} color={textColor} label={t('admin.partner_register_phone')} />
      <Flex gap={2} alignItems="center">
        <Menu>
          <MenuButton as={Button} variant="outline" colorScheme="gray" rightIcon={<ChevronDownIcon />}>
            <Flex align="center" gap={2}>
              <Image src={selectedCountry.flag} boxSize="20px" borderRadius="full" />
              <Text marginRight={'30px'}>{selectedCountry.ddi}</Text>
            </Flex>
          </MenuButton>
          <MenuList>
            {countries.map((country) => (
              <MenuItem key={country.code} onClick={() => handleDdiChange(country)}>
                <Flex align="center" gap={2}>
                  <Image src={country.flag} boxSize="20px" borderRadius="full" />
                  <Text>{country.ddi} - {country.names[i18n.language as keyof typeof country.names]}</Text>
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Input
          placeholder={getPlaceholder()}
          type="tel"
          value={value.replace(/^\+\d+\s?/, "")}
          onChange={handlePhoneChange}
        />
      </Flex>
    </Flex >
  );
};

export default PhoneInput;
